import { useContext, useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { FullTrip, GeoPoint } from 'tripPlanner/types/common'
import type { PublicFullTrip, PublicTripItem } from 'tripPlanner/types/publicTrip'
import type { MarkerItem, RouteItem, TripItem } from 'tripPlanner/types/tripItem'
import RoutePolyline from './RoutePolyline'
import { isPinnedItem, isRouteItem } from '../utils'
import { getItemPlace } from 'tripPlanner/utils'
import { getDayColor, PIN_COLORS } from 'tripPlanner/utils/tripItemPin'
import MapLayersContext from 'tripPlanner/contexts/MapLayers/MapLayersContext'
import { hasGeoPlace, hasValidPlace, isDateLayerVisible } from './DirectionRouteUtil'
import { EmptyArray } from 'lib/array/arrayUtils'
import type { TravelMode } from 'tripPlanner/api/routes/getRouteInfo'

interface DayRoutesProps {
  trip: FullTrip | PublicFullTrip
  date?: string
  selectedTripItemId?: string
}

interface RoutePoint {
  origin: GeoPoint
  destination: GeoPoint
  id: string
  color: string
  travelMode?: TravelMode
}

function toTravelMode(pref?: string): TravelMode | undefined {
  return pref === 'DRIVE' || pref === 'WALK' ? pref : undefined
}

function selectSegmentMode(
  isFromAccommodation: boolean,
  nextIsFirst: boolean,
  fromPref?: string,
  toPref?: string,
  toFromHotelPref?: string,
): TravelMode | undefined {
  if (isFromAccommodation) {
    return toTravelMode(toFromHotelPref)
  }
  if (nextIsFirst) {
    return toTravelMode(toPref)
  }
  return toTravelMode(fromPref)
}

function DayRoutes({ trip, date, selectedTripItemId }: DayRoutesProps) {
  const { layerGroups } = useContext(MapLayersContext)

  function isMarkerOrRouteItem(
    item: TripItem | PublicTripItem,
  ): item is MarkerItem | RouteItem {
    if (item.type === 'NOTE') return false
    if (isPinnedItem(item)) return hasValidPlace(item)
    if (isRouteItem(item)) return hasGeoPlace(item.startPlace) && hasGeoPlace(item.endPlace)
    return false
  }

  const dayItems = useMemo<Array<MarkerItem | RouteItem>>(() => {
    if (!date) return []

    const isSameDay = (itemDate?: moment.Moment) =>
      itemDate?.format(ISO_DATE_FORMAT).startsWith(date)

    const hotelItem = trip.items.find(item =>
      item.type === 'ACCOMMODATION' &&
      item.startDate &&
      item.endDate &&
      moment(date).isBetween(item.startDate, item.endDate, 'day', '[]'),
    )

    const items = trip.items
      .filter((item) => isSameDay(item.startDate))
      .filter(isMarkerOrRouteItem)
      .sort((a, b) => a.itineraryRankString.localeCompare(b.itineraryRankString))

    let itemsWithHotel: Array<MarkerItem | RouteItem> = items
    if (hotelItem?.type === 'ACCOMMODATION' && !items.some(item => item.id === hotelItem.id)) {
      if (isPinnedItem(hotelItem)) {
        itemsWithHotel = [hotelItem, ...items]
      }
    }

    return itemsWithHotel
  }, [trip.items, date])

  const isDateVisible = useMemo(
    () => isDateLayerVisible(layerGroups, date),
    [layerGroups, date],
  )

  const dayColor = useMemo(() => {
    const secondItem = dayItems[1]
    if (!secondItem?.startDate) {
      return PIN_COLORS.SUNDAY
    }
    return getDayColor(moment(secondItem.startDate))
  }, [dayItems])

  const routePoints = useMemo((): Array<RoutePoint> => {
    if (!isDateVisible) return EmptyArray

    const buildRoutePoint = (item: MarkerItem | RouteItem, nextItem: MarkerItem | RouteItem): RoutePoint | null => {
      if (item.type === 'FLIGHT' && nextItem.type === 'FLIGHT') return null

      const fromPlace = item.type === 'FLIGHT' ? getItemPlace(item, 'end') : getItemPlace(item, 'start')
      const toPlace = getItemPlace(nextItem, 'start')
      if (!fromPlace || !toPlace || fromPlace.type !== 'GEO' || toPlace.type !== 'GEO') return null

      const isFromAccommodation = item.type === 'ACCOMMODATION'
      const nextIsFirst = false
      const segmentMode = selectSegmentMode(
        isFromAccommodation,
        nextIsFirst,
        item.travelPreferences,
        nextItem.travelPreferences,
        nextItem.fromHotelTravelPreferences,
      )

      return {
        origin: fromPlace.point,
        destination: toPlace.point,
        id: `${item.id}-${nextItem.id}`,
        color: dayColor,
        travelMode: segmentMode,
      }
    }

    if (selectedTripItemId) {
      const idx = dayItems.findIndex(it => it.id === selectedTripItemId)
      if (idx >= 1) {
        const point = buildRoutePoint(dayItems[idx - 1], dayItems[idx])
        return point ? [point] : EmptyArray
      }
      return EmptyArray
    }

    return dayItems.reduce<Array<RoutePoint>>((points, item, index) => {
      if (index >= dayItems.length - 1) return points
      const nextItem = dayItems[index + 1]
      const point = buildRoutePoint(item, nextItem)
      if (point) points.push(point)
      return points
    }, [])
  }, [dayColor, dayItems, isDateVisible, selectedTripItemId])
  if (!routePoints.length) {
    return null
  }
  return (
    <>
      {routePoints.map(({ origin, destination, id, color, travelMode }) => (
        <RoutePolyline
          key={id}
          origin={origin}
          destination={destination}
          color={color}
          travelMode={travelMode}
        />
      ))}
    </>
  )
}

export default DayRoutes
